import type { CSSProperties, PropsWithChildren } from 'react';
import { forwardRef, memo } from 'react';

import type { PopupActions, PopupProps } from 'reactjs-popup/dist/types';

import useLockScroll from '@polyai/common/hooks/useLockScroll';

import Button from 'components/atoms/Button';
import { useBreakpoint } from '../../../hooks/useBreakpoint';

import 'reactjs-popup/dist/index.css';

import * as Styled from './LegacyModal.styled';

/**
 * @deprecated This component is deprecated in favor of the molecules/Modal
 */
export type ModalProps = Omit<PopupProps, 'modal'> & {
  size?: keyof typeof Styled.ModalSize;
  overflow?: CSSProperties['overflow'];
};

/**
 * @deprecated This component is deprecated in favor of the molecules/Modal
 */
export { ModalBody, ModalButtonsContainer } from './LegacyModal.styled';

const LockScroll = () => {
  useLockScroll(true);

  return null;
};

/**
 * @deprecated This component is deprecated in favor of the molecules/Modal
 * @return {React.JSX.Element}
 */
export const ModalButtons = ({
  children,
  goBack,
  backButtonText = 'Back',
  ...rest
}: PropsWithChildren<{
  goBack?: () => void;
  backButtonText?: string;
}>) => {
  const isSMOrLarger = useBreakpoint('xs');
  const size = !isSMOrLarger ? 'sm' : undefined;
  return (
    <Styled.ModalButtonsContainer
      size={size}
      {...rest}
      $hasBackButton={!!goBack}
    >
      {goBack && (
        <Button label={backButtonText} variant="GHOST" onClick={goBack} />
      )}
      <Styled.ActionButtonsContainer size={size}>
        {children}
      </Styled.ActionButtonsContainer>
    </Styled.ModalButtonsContainer>
  );
};

/**
 * @deprecated This component is deprecated in favor of the molecules/Modal
 * @example <Modal.Root>
 *   <Modal.Trigger>Open Modal</Modal.Trigger>
 *   <Modal.Content>
 *     <Modal.Title>Title</Modal.Title>
 *     Body
 *     <Modal.Footer
 *       dismiss={{ onClick: () => {} }}
 *       primary={{ onClick: () => {} }}
 *       secondary={{ onClick: () => {} }}
 *     />
 *   </Modal.Content>
 * </Modal.Root>
 */
const LegacyModal = memo(
  forwardRef<PopupActions, ModalProps>(
    ({ children, size = 'md', overflow, ...props }, ref) => {
      return (
        <Styled.Popup
          modal={true}
          {...props}
          ref={ref}
          $overflow={overflow}
          $size={Styled.ModalSize[size]}
        >
          <LockScroll />
          {children}
        </Styled.Popup>
      );
    },
  ),
);

export default LegacyModal;
